import { Core } from '@/api/Core/Core';
import type {
  DailyImageBatch,
  GenerationRecord,
  GenerateProductImageRequest,
  ImageDescriptor,
  ImageBatchItem,
  ProductImageDetail,
  ProductImageSummary,
  ImageListQuery,
  PagedImages,
  SuspendProductImageRequest,
} from '@/api/ProductImages/types';
import {
  validateBatchItem,
  validateBatch,
  validateGeneration,
  validateImageDescriptor,
  validateProductDetail,
  validateProductSummary,
  validatePage,
} from '@/api/ProductImages/validateResponse';

import { ProductImageApiError } from '@/api/ProductImages/ProductImageApiError';
export { ProductImageApiError } from '@/api/ProductImages/ProductImageApiError';

const unwrap = <T>(response: {
  success: boolean;
  code: number;
  data?: T;
  message?: unknown;
}): T => {
  if (
    response.code >= 400 ||
    response.success === false ||
    response.data === undefined
  ) {
    const message =
      typeof response.message === 'object' && response.message !== null
        ? ((response.message as { Message?: unknown; message?: unknown })
            .Message ?? (response.message as { message?: unknown }).message)
        : response.message;
    throw new ProductImageApiError(
      response.code || 500,
      typeof message === 'string' ? message : undefined,
    );
  }
  return response.data;
};

const privateConfig = (signal?: AbortSignal) => ({
  signal,
  timeout: 30_000,
  headers: { 'Cache-Control': 'no-store' },
});

class ProductImages extends Core {
  products = async (query: ImageListQuery) => {
    const result = unwrap(
      await this.get<PagedImages<ProductImageSummary>>(
        'ProductImages/products',
        query,
        privateConfig(),
      ),
    );
    validatePage(result, validateProductSummary);
    return result;
  };

  product = async (id: number) =>
    validateProductDetail(
      unwrap(
        await this.get<ProductImageDetail>(
          `ProductImages/products/${id}`,
          undefined,
          privateConfig(),
        ),
      ),
    );

  generate = async (id: number, request: GenerateProductImageRequest) =>
    validateGeneration(
      unwrap(
        await this.post<GenerationRecord>(
          `ProductImages/products/${id}/generate`,
          request,
          privateConfig(),
        ),
      ),
    );

  suspend = async (
    id: number,
    revisionId: string,
    request: SuspendProductImageRequest,
  ) =>
    validateProductDetail(
      unwrap(
        await this.post<ProductImageDetail>(
          `ProductImages/products/${id}/revisions/${encodeURIComponent(revisionId)}/suspend`,
          request,
          privateConfig(),
        ),
      ),
    );

  retryVariant = async (
    id: number,
    variantKey: string,
    request: GenerateProductImageRequest,
  ) =>
    validateGeneration(
      unwrap(
        await this.post<GenerationRecord>(
          `ProductImages/products/${id}/variants/${encodeURIComponent(variantKey)}/retry`,
          request,
          privateConfig(),
        ),
      ),
    );

  generationContent = async (id: string, signal?: AbortSignal) =>
    unwrap(
      await this.get<Blob>(
        `ProductImages/generations/${encodeURIComponent(id)}/content`,
        undefined,
        { ...privateConfig(signal), responseType: 'blob' },
      ),
    );

  batches = async (query: ImageListQuery) => {
    const result = unwrap(
      await this.get<PagedImages<DailyImageBatch>>(
        'ProductImages/batches',
        query,
        privateConfig(),
      ),
    );
    validatePage(result, validateBatch);
    return result;
  };

  batch = async (id: string, query: ImageListQuery) => {
    const result = unwrap(
      await this.get<PagedImages<ImageBatchItem> & { batch: DailyImageBatch }>(
        `ProductImages/batches/${encodeURIComponent(id)}`,
        query,
        privateConfig(),
      ),
    );
    validatePage(result, validateBatchItem);
    validateBatch(result.batch);
    return result;
  };

  generation = async (id: string) =>
    validateGeneration(
      unwrap(
        await this.get<GenerationRecord>(
          `ProductImages/generations/${encodeURIComponent(id)}`,
          undefined,
          privateConfig(),
        ),
      ),
    );

  representative = async (vehicleId: number, signal?: AbortSignal) => {
    const response = await this.get<ImageDescriptor>(
      `ProductRepresentativeImages/vehicles/${vehicleId}`,
      undefined,
      privateConfig(signal),
    );
    if (response.code === 204) return null;
    return validateImageDescriptor(unwrap(response));
  };

  vehicleContent = async (
    vehicleId: number,
    assetId: string,
    signal?: AbortSignal,
  ) =>
    unwrap(
      await this.get<Blob>(
        `ProductRepresentativeImages/vehicles/${vehicleId}/assets/${encodeURIComponent(assetId)}/content`,
        undefined,
        { ...privateConfig(signal), responseType: 'blob' },
      ),
    );

  revisionContent = async (assetId: string, signal?: AbortSignal) =>
    unwrap(
      await this.get<Blob>(
        `ProductImages/assets/${encodeURIComponent(assetId)}/content`,
        undefined,
        { ...privateConfig(signal), responseType: 'blob' },
      ),
    );

  candidateContent = async (
    generationId: string,
    number: number,
    signal?: AbortSignal,
  ) =>
    unwrap(
      await this.get<Blob>(
        `ProductImages/generations/${encodeURIComponent(generationId)}/candidates/${number}/content`,
        undefined,
        { ...privateConfig(signal), responseType: 'blob' },
      ),
    );
}

export const productImages = new ProductImages();

export const isPrivateImage = (blob: Blob) =>
  blob instanceof Blob &&
  blob.size > 0 &&
  blob.size <= 20 * 1024 * 1024 &&
  ['image/jpeg', 'image/png', 'image/webp'].includes(blob.type);
